/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Paper,
  Alert,
  Chip,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  createObjectives,
  updateObjectives,
  fetchObjectives,
  fetchEventCompliances,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { ContentComplianceStatus } from 'src/v3/core/types'

type Objective = {
  id?: string
  text: string
}

type ObjectivesManagementProps = {
  eventID: string
  objectives?: {
    id: string
    syllabusID: string
    text: string
    sequence: number
    createdTime: string | null
    updatedTime: string | null
  }[]
  objectivesStatus?: string
  complianceStatus?: ContentComplianceStatus
}

export default ({
  eventID,
  objectives = [],
  complianceStatus,
}: ObjectivesManagementProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [loading, setLoading] = useState(false)

  let hasExistingObjectives = objectives.length > 0

  let form = useForm({
    defaultValues: {
      objectives: hasExistingObjectives
        ? objectives.map((obj) => ({ text: obj.text }))
        : [{ text: '' }],
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        // Filter out empty objectives
        let validObjectives = value.objectives.filter(
          (obj) => obj.text.trim().length > 0,
        )

        if (validObjectives.length === 0) {
          setLoading(false)
          return
        }

        if (hasExistingObjectives) {
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(
            updateObjectives({
              eventID,
              objectives: validObjectives,
            }),
          )
        } else {
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(
            createObjectives({
              eventID,
              objectives: validObjectives,
            }),
          )
        }

        // Refetch objectives and compliance statuses
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchObjectives(eventID))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchEventCompliances())
        setIsEditing(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save objectives:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  let addObjective = () => {
    let currentObjectives = form.getFieldValue('objectives') || []
    form.setFieldValue('objectives', [...currentObjectives, { text: '' }])
  }

  let removeObjective = (index: number) => {
    let currentObjectives = form.getFieldValue('objectives') || []
    if (currentObjectives.length > 1) {
      form.setFieldValue(
        'objectives',
        currentObjectives.filter((_, i) => i !== index),
      )
    }
  }

  let startEditing = () => {
    // Reset form with current objectives
    form.setFieldValue(
      'objectives',
      hasExistingObjectives
        ? objectives.map((obj) => ({ text: obj.text }))
        : [{ text: '' }],
    )
    setIsEditing(true)
  }

  let cancelEditing = () => {
    setIsEditing(false)
    form.reset()
  }

  if (!isEditing && !hasExistingObjectives) {
    return (
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.paperDark',
          borderRadius: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          No Learning Objectives Added
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            mb: 2,
          }}
        >
          Define specific, measurable learning objectives for your course.
        </Typography>
        <Button
          variant="contained"
          onClick={startEditing}
          aria-label="Add learning objectives"
        >
          Add Learning Objectives
        </Button>
      </Box>
    )
  }

  if (!isEditing && hasExistingObjectives) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h4">
            Learning Objectives ({objectives.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={startEditing}
            aria-label="Edit learning objectives"
          >
            Edit Objectives
          </Button>
        </Box>
        {/* Objectives Compliance Status */}
        {complianceStatus && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              p: 2,
              bgcolor: 'background.paperDark',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'medium',
              }}
            >
              Learning Objectives Status
            </Typography>
            <Chip
              label={
                complianceStatus === 'approved'
                  ? 'Approved'
                  : complianceStatus === 'rejected'
                    ? 'Rejected'
                    : complianceStatus === 'updated'
                      ? 'Updated - Pending Review'
                      : complianceStatus === 'unapproved'
                        ? 'Unapproved'
                        : 'Pending Review'
              }
              variant="outlined"
              sx={{
                color:
                  complianceStatus === 'approved'
                    ? 'success'
                    : complianceStatus === 'rejected' ||
                        complianceStatus === 'unapproved'
                      ? 'error'
                      : 'primary',
              }}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {objectives.map((objective, index) => (
            <Paper key={objective.id} variant="outlined" sx={{ p: 2 }}>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  mb: 0.5,
                }}
              >
                Objective {index + 1}
              </Typography>
              <Typography variant="body1">{objective.text}</Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    )
  }

  // Editing mode
  return (
    <Box>
      <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
        {hasExistingObjectives
          ? 'Edit Learning Objectives'
          : 'Add Learning Objectives'}
      </Typography>

      <form.Field
        name="objectives"
        children={(field) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {(field.state.value as Objective[]).map((objective, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={`Learning Objective ${index + 1}`}
                  value={objective.text}
                  onChange={(e) => {
                    let updatedObjectives = [
                      ...(field.state.value as Objective[]),
                    ]
                    updatedObjectives[index] = {
                      ...updatedObjectives[index],
                      text: e.target.value,
                    }
                    field.handleChange(updatedObjectives)
                  }}
                  sx={{ bgcolor: 'background.paperDark' }}
                  placeholder="Enter a specific, measurable learning objective (e.g., Upon completion, learners will be able to...)"
                  disabled={loading}
                />
                <IconButton
                  onClick={() => removeObjective(index)}
                  disabled={
                    (field.state.value as Objective[]).length <= 1 || loading
                  }
                  aria-label={`Remove objective ${index + 1}`}
                  sx={{ mt: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      />

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addObjective}
          disabled={loading}
          aria-label="Add another objective"
        >
          Add Objective
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Write clear, specific learning objectives using action verbs. Focus on
        what learners will be able to do after completing the course.
      </Alert>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          onClick={() => form.handleSubmit()}
          disabled={loading}
          aria-label="Save learning objectives"
        >
          {loading
            ? 'Saving...'
            : hasExistingObjectives
              ? 'Update Objectives'
              : 'Save Objectives'}
        </Button>
        <Button
          variant="outlined"
          onClick={cancelEditing}
          disabled={loading}
          aria-label="Cancel editing objectives"
        >
          Cancel
        </Button>
      </Box>
    </Box>
  )
}
